// src/components/proposal-formats/FormatPaymentMilestoneRow.tsx

import { useEffect, useState } from "react";
import type { FormatPaymentMilestone } from "@/lib/opportunity/formatPaymentMilestoneTypes";
import type { UpsertFormatPaymentMilestonePayload } from "@/lib/opportunity/formatPaymentMilestoneClient";
import {
  PAYMENT_LINE_ITEM_BASE_REF,
  type FormatPaymentAmountMode,
} from "@/lib/opportunity/formatPaymentMilestoneTypes";
import { formatOpportunityCurrency } from "@/lib/opportunity/formatCurrency";

const AMOUNT_MODE_LABELS: Record<FormatPaymentAmountMode, string> = {
  percent_of_remaining: "% of remaining",
  percent_of_total: "% of total",
  fixed: "Fixed $",
  line_item_amount: "Line item $",
};

export type PaymentLineItemOption = { ref: string; label: string };

export type FormatPaymentMilestoneRowProps = {
  milestone: FormatPaymentMilestone;
  previewAmount: number | null;
  lineItemOptions: PaymentLineItemOption[];
  canMoveUp: boolean;
  canMoveDown: boolean;
  onSave: (
    payload: UpsertFormatPaymentMilestonePayload,
  ) => Promise<{ ok: boolean; error?: string }>;
  onDelete?: (id: string) => Promise<{ ok: boolean; error?: string }>;
  onMove: (direction: "up" | "down") => void;
};

export function FormatPaymentMilestoneRow(props: FormatPaymentMilestoneRowProps) {
  const { milestone, previewAmount, lineItemOptions, canMoveUp, canMoveDown, onSave, onDelete, onMove } =
    props;
  const isCustom = milestone.milestone_role === "custom";

  const [label, setLabel] = useState(milestone.label);
  const [subtext, setSubtext] = useState(milestone.subtext ?? "");
  const [amountMode, setAmountMode] = useState<FormatPaymentAmountMode>(
    milestone.amount_mode ?? "percent_of_remaining",
  );
  const [defaultAmount, setDefaultAmount] = useState(
    String(milestone.default_amount ?? (isCustom ? 10 : 0)),
  );
  const [lineItemRef, setLineItemRef] = useState(
    milestone.line_item_ref ?? lineItemOptions[0]?.ref ?? PAYMENT_LINE_ITEM_BASE_REF,
  );
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    setLabel(milestone.label);
    setSubtext(milestone.subtext ?? "");
    setAmountMode(milestone.amount_mode ?? "percent_of_remaining");
    setDefaultAmount(String(milestone.default_amount ?? (isCustom ? 10 : 0)));
    setLineItemRef(
      milestone.line_item_ref ?? lineItemOptions[0]?.ref ?? PAYMENT_LINE_ITEM_BASE_REF,
    );
    setMsg(null);
  }, [milestone.id, milestone.updated_at, isCustom, lineItemOptions]);

  const dirty =
    label !== milestone.label ||
    subtext !== (milestone.subtext ?? "") ||
    (isCustom &&
      (amountMode !== milestone.amount_mode ||
        defaultAmount !== String(milestone.default_amount ?? 0) ||
        lineItemRef !== (milestone.line_item_ref ?? "")));

  async function handleSave() {
    if (!label.trim()) return;
    let parsedAmount: number | null = null;
    let mode: FormatPaymentAmountMode | null = null;
    let ref: string | null = null;
    if (isCustom) {
      mode = amountMode;
      if (amountMode === "line_item_amount") {
        ref = lineItemRef;
      } else {
        const amt = Number(defaultAmount);
        if (!Number.isFinite(amt) || amt < 0) return;
        if (
          (amountMode === "percent_of_remaining" || amountMode === "percent_of_total") &&
          amt > 100
        ) {
          return;
        }
        parsedAmount = amt;
      }
    }
    setBusy(true);
    setMsg(null);
    const r = await onSave({
      id: milestone.id.startsWith("__default_") ? undefined : milestone.id,
      format_key: milestone.format_key,
      product_type: milestone.product_type,
      milestone_role: milestone.milestone_role,
      sort_order: milestone.sort_order,
      label: label.trim(),
      subtext: subtext.trim() || null,
      amount_mode: mode,
      default_amount: parsedAmount,
      line_item_ref: ref,
    });
    setBusy(false);
    setMsg({ ok: r.ok, text: r.ok ? "Saved." : (r.error || "Save failed.") });
  }

  async function handleDelete() {
    if (!onDelete || !window.confirm(`Remove milestone "${milestone.label}"?`)) return;
    setBusy(true);
    const r = await onDelete(milestone.id);
    setBusy(false);
    if (!r.ok) setMsg({ ok: false, text: r.error || "Delete failed." });
  }

  const roleLabel =
    milestone.milestone_role === "deposit"
      ? "Deposit"
      : milestone.milestone_role === "balance"
        ? "Final balance"
        : "Custom";

  return (
    <details className="rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900 group" open>
      <summary className="cursor-pointer list-none px-3 py-2 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <span className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-zinc-500">
            {roleLabel}
          </span>
          <p className="text-sm font-medium text-gray-900 dark:text-zinc-100 truncate">{label}</p>
        </div>
        {previewAmount != null ? (
          <span className="text-xs tabular-nums text-gray-500 shrink-0">
            {formatOpportunityCurrency(previewAmount)}
          </span>
        ) : null}
      </summary>

      <div className="px-3 pb-3 space-y-2 border-t border-gray-100 dark:border-zinc-800 pt-2">
        <label className="block text-xs">
          <span className="text-gray-500">Label</span>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 dark:bg-zinc-800 dark:border-zinc-700"
          />
        </label>
        <label className="block text-xs">
          <span className="text-gray-500">Subtext (optional)</span>
          <input
            type="text"
            value={subtext}
            onChange={(e) => setSubtext(e.target.value)}
            className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 dark:bg-zinc-800 dark:border-zinc-700"
          />
        </label>

        {isCustom ? (
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={amountMode}
              onChange={(e) => setAmountMode(e.target.value as FormatPaymentAmountMode)}
              className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 dark:bg-zinc-800 dark:border-zinc-700"
            >
              {(Object.keys(AMOUNT_MODE_LABELS) as FormatPaymentAmountMode[]).map((mode) => (
                <option key={mode} value={mode}>
                  {AMOUNT_MODE_LABELS[mode]}
                </option>
              ))}
            </select>
            {amountMode === "line_item_amount" ? (
              <select
                value={lineItemRef}
                onChange={(e) => setLineItemRef(e.target.value)}
                className="min-w-[140px] text-sm border border-gray-200 rounded-lg px-2 py-1.5 dark:bg-zinc-800 dark:border-zinc-700"
              >
                {lineItemOptions.map((opt) => (
                  <option key={opt.ref} value={opt.ref}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="number"
                min={0}
                max={
                  amountMode === "percent_of_remaining" || amountMode === "percent_of_total"
                    ? 100
                    : undefined
                }
                step="0.01"
                value={defaultAmount}
                onChange={(e) => setDefaultAmount(e.target.value)}
                className="w-24 text-sm border border-gray-200 rounded-lg px-2 py-1.5 dark:bg-zinc-800 dark:border-zinc-700"
              />
            )}
          </div>
        ) : null}

        <div className="flex items-center justify-between gap-2 pt-1">
          <div className="flex gap-1">
            <button
              type="button"
              disabled={!canMoveUp || busy}
              onClick={() => onMove("up")}
              className="px-2 py-0.5 rounded border border-gray-300 text-[10px] disabled:opacity-40"
            >
              ↑
            </button>
            <button
              type="button"
              disabled={!canMoveDown || busy}
              onClick={() => onMove("down")}
              className="px-2 py-0.5 rounded border border-gray-300 text-[10px] disabled:opacity-40"
            >
              ↓
            </button>
            {isCustom && onDelete ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => void handleDelete()}
                className="px-2 py-0.5 rounded border border-red-300 text-[10px] text-red-700"
              >
                Remove
              </button>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {msg ? (
              <span className={msg.ok ? "text-emerald-600 text-xs" : "text-red-600 text-xs"}>
                {msg.text}
              </span>
            ) : null}
            <button
              type="button"
              disabled={busy || !dirty}
              onClick={() => void handleSave()}
              className="px-2.5 py-1 rounded-lg bg-indigo-600 text-white text-xs disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </details>
  );
}
